(() => {
  const selector = '#header ul.dt-nav.dt-desktop-menu';
  const controllers = new Map();

  class NovaDesktopNavigation {
    constructor(nav) {
      this.nav = nav;
      this.abort = new AbortController();
      this.desktop = window.matchMedia('(min-width: 1280px)');
      this.entries = new Map();
      this.panels = new Map();
      this.positionFrame = 0;
      this.header = nav.closest('#header');

      nav.querySelectorAll('li.menu-item-has-children').forEach((item) => {
        const panel = item.querySelector(':scope > .sub-menu-block');
        const toggle = item.querySelector(':scope > [data-nova-nav-toggle]');
        if (!panel || !toggle) return;
        const entry = { item, panel, toggle, open: false, pinned: false, returnFocus: toggle,
          mega: item.matches('.nova-catalog-menu, .nova-brands-menu'), enterTimer: 0, leaveTimer: 0 };
        this.entries.set(item, entry);
        this.panels.set(panel, entry);
      });

      this.entries.forEach((entry) => this.bindPointer(entry));
      this.listen(nav, 'click', (event) => this.onClick(event));
      this.listen(nav, 'keydown', (event) => this.onKeyDown(event));
      this.listen(nav, 'focusin', (event) => this.onFocusIn(event));
      this.listen(nav, 'focusout', (event) => this.onFocusOut(event));
      this.listen(document, 'click', (event) => {
        if (this.active && !nav.contains(event.target)) this.closeAll();
      }, true);
      this.listen(document, 'keydown', (event) => {
        if (!this.active || event.key !== 'Escape' || nav.contains(event.target)) return;
        this.entries.forEach((entry) => { if (entry.mega) this.clearTimers(entry); });
        const entry = Array.from(this.entries.values()).filter((item) => item.open).at(-1);
        if (entry) {
          this.close(entry);
          entry.returnFocus.focus({ preventScroll: true });
          event.preventDefault();
        }
      });
      this.listen(this.desktop, 'change', () => this.syncBreakpoint());
      this.listen(window, 'resize', () => this.schedulePosition());
      this.listen(window, 'scroll', () => this.schedulePosition());
      if (this.header && Array.from(this.entries.values()).some((entry) => entry.mega)) {
        this.resizeObserver = new ResizeObserver(() => this.schedulePosition());
        this.resizeObserver.observe(this.header);
        this.resizeObserver.observe(nav);
      }
      this.syncBreakpoint();
    }

    listen(target, type, callback, capture = false) {
      target.addEventListener(type, callback, { signal: this.abort.signal, capture });
    }

    bindPointer(entry) {
      this.listen(entry.item, 'pointerenter', (event) => {
        if (!this.active || event.pointerType === 'touch' || this.hasFocusedMegaPanel(entry)) return;
        this.clearTimers(entry);
        if (entry.mega && !entry.open) {
          entry.enterTimer = window.setTimeout(() => {
            if (this.active && entry.item.matches(':hover') && !this.hasFocusedMegaPanel(entry)) this.open(entry);
          }, 120);
        } else this.open(entry);
      });
      this.listen(entry.item, 'pointerleave', (event) => {
        if (!this.active || event.pointerType === 'touch') return;
        this.clearTimers(entry);
        if (entry.pinned) return;
        if (entry.mega) {
          entry.leaveTimer = window.setTimeout(() => {
            if (!entry.pinned && !entry.item.contains(document.activeElement)) this.close(entry);
          }, 200);
        } else this.close(entry);
      });
    }

    clearTimers(entry) {
      window.clearTimeout(entry.enterTimer);
      window.clearTimeout(entry.leaveTimer);
      entry.enterTimer = entry.leaveTimer = 0;
    }

    hasFocusedMegaPanel(nextEntry) {
      return Array.from(this.entries.values()).some((entry) =>
        entry !== nextEntry && entry.mega && entry.open && entry.panel.contains(document.activeElement)
      );
    }

    positionMegaPanel(entry) {
      if (!entry.mega || !this.header || !entry.panel.offsetParent) return;
      const header = this.header.getBoundingClientRect();
      const origin = entry.panel.offsetParent.getBoundingClientRect();
      const trigger = entry.item.getBoundingClientRect();
      const center = entry.item.classList.contains('nova-brands-menu')
        ? trigger.left + trigger.width / 2
        : header.left + header.width / 2;
      entry.panel.style.setProperty('--nova-menu-left', `${center - origin.left}px`);
      entry.panel.style.setProperty('--nova-menu-top', `${header.bottom - origin.top}px`);
    }

    schedulePosition() {
      if (!this.active || this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = 0;
        this.entries.forEach((entry) => { if (entry.open) this.positionMegaPanel(entry); });
      });
    }

    syncBreakpoint() {
      this.active = this.desktop.matches;
      this.entries.forEach((entry) => {
        this.close(entry);
        if (!this.active) {
          delete entry.item.dataset.novaNavOpen;
          entry.panel.removeAttribute('aria-hidden');
          entry.panel.inert = false;
        }
      });
      if (this.active) this.nav.dataset.novaNavigation = 'desktop';
      else delete this.nav.dataset.novaNavigation;
    }

    parent(entry) {
      const item = entry.item.parentElement.closest('li.menu-item-has-children');
      return this.entries.get(item);
    }

    setOpen(entry, open) {
      this.clearTimers(entry);
      entry.open = open;
      if (!open) entry.pinned = false;
      entry.item.dataset.novaNavOpen = String(open);
      entry.toggle.setAttribute('aria-expanded', String(open));
      entry.panel.setAttribute('aria-hidden', String(!open));
      entry.panel.inert = !open;
      entry.panel.classList.toggle('is-hidden', !open);
      entry.item.querySelector(':scope > a')?.classList.toggle('selected', open);
      if (open) this.positionMegaPanel(entry);
    }

    open(entry, returnFocus) {
      const parent = this.parent(entry);
      if (parent && !parent.open) this.open(parent);
      this.entries.forEach((sibling) => {
        if (sibling !== entry && this.parent(sibling) === parent) this.close(sibling);
      });
      if (returnFocus) entry.returnFocus = returnFocus;
      this.setOpen(entry, true);
    }

    close(entry) {
      this.entries.forEach((child) => {
        if (child !== entry && entry.item.contains(child.item)) this.setOpen(child, false);
      });
      this.setOpen(entry, false);
    }

    closeAll() {
      this.entries.forEach((entry) => this.setOpen(entry, false));
    }

    pin(entry, source) {
      this.open(entry, source);
      for (let current = entry; current; current = this.parent(current)) current.pinned = true;
    }

    entryAt(target) {
      return this.entries.get(target.closest('li.menu-item-has-children'));
    }

    onClick(event) {
      if (!this.active) return;
      const toggle = event.target.closest('[data-nova-nav-toggle]');
      if (!toggle || !this.nav.contains(toggle)) return;
      const entry = this.entryAt(toggle);
      if (!entry || entry.toggle !== toggle) return;
      event.preventDefault();
      if (entry.open && entry.pinned) this.close(entry);
      else this.pin(entry, toggle);
    }

    onFocusIn(event) {
      if (!this.active) return;
      this.entries.forEach((openEntry) => {
        if (openEntry.mega || openEntry.item.contains(event.target)) this.clearTimers(openEntry);
        if (openEntry.open && !openEntry.item.contains(event.target)) {
          this.close(openEntry);
        }
      });
    }

    onFocusOut(event) {
      this.entries.forEach((entry) => {
        if (!entry.mega || !entry.item.contains(event.target) || entry.item.contains(event.relatedTarget)) return;
        this.clearTimers(entry);
        if (!this.nav.contains(event.relatedTarget)) this.close(entry);
      });
      if (event.relatedTarget && this.nav.contains(event.relatedTarget)) return;
      requestAnimationFrame(() => {
        if (this.active && !this.nav.contains(document.activeElement)) this.closeAll();
      });
    }

    panelTargets(panel) {
      return Array.from(panel.querySelectorAll('a[href], button[data-nova-nav-toggle]')).filter((target) => {
        if (target.closest('.sub-menu-block') !== panel) return false;
        return !target.classList.contains('nova-navigation-toggle');
      });
    }

    rootTargets() {
      return Array.from(this.nav.children).map((item) =>
        item.querySelector(':scope > a, :scope > button.nova-navigation-parent')
      ).filter(Boolean);
    }

    focusAt(targets, current, offset) {
      if (!targets.length) return;
      let index = targets.indexOf(current);
      if (index < 0) index = targets.findIndex((target) => target.parentElement === current.parentElement);
      targets[(index + offset + targets.length) % targets.length].focus();
    }

    enter(entry, source, last = false) {
      this.pin(entry, source);
      const targets = this.panelTargets(entry.panel);
      const target = last ? targets.at(-1) : targets[0];
      if (entry.mega) {
        target?.focus();
        return;
      }
      requestAnimationFrame(() => {
        if (entry.open && this.active) target?.focus();
      });
    }

    escape(target) {
      this.entries.forEach((entry) => { if (entry.mega) this.clearTimers(entry); });
      let entry = this.entryAt(target);
      while (entry && !entry.open) entry = this.parent(entry);
      if (!entry) entry = Array.from(this.entries.values()).filter((item) => item.open).at(-1);
      if (!entry) return false;
      this.close(entry);
      entry.returnFocus.focus({ preventScroll: true });
      return true;
    }

    onKeyDown(event) {
      if (!this.active) return;
      const target = event.target.closest('a, button');
      if (!target || !this.nav.contains(target)) return;
      const panel = target.closest('.sub-menu-block');
      const entry = this.entryAt(target);
      const targets = panel ? this.panelTargets(panel) : this.rootTargets();
      let handled = false;

      if (event.key === 'Escape') handled = this.escape(target);
      else if (event.key === 'Home' || event.key === 'End') {
        (event.key === 'Home' ? targets[0] : targets.at(-1))?.focus();
        handled = true;
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (panel) {
          this.focusAt(targets, target, event.key === 'ArrowDown' ? 1 : -1);
          handled = true;
        } else if (entry && target.parentElement === entry.item) {
          this.enter(entry, target, event.key === 'ArrowUp');
          handled = true;
        }
      } else if (event.key === 'ArrowRight') {
        if (panel && entry && target.parentElement === entry.item) this.enter(entry, target);
        else if (!panel) this.focusAt(targets, target, 1);
        else return;
        handled = true;
      } else if (event.key === 'ArrowLeft') {
        if (panel) {
          const owner = this.panels.get(panel);
          if (owner) {
            this.close(owner);
            owner.returnFocus.focus({ preventScroll: true });
          }
        } else this.focusAt(targets, target, -1);
        handled = true;
      }

      if (handled) event.preventDefault();
    }

    destroy() {
      this.abort.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.positionFrame);
      this.active = false;
      this.closeAll();
      delete this.nav.dataset.novaNavigation;
      this.entries.forEach((entry) => {
        delete entry.item.dataset.novaNavOpen;
        entry.panel.removeAttribute('aria-hidden');
        entry.panel.inert = false;
      });
    }
  }

  function mount(root = document) {
    controllers.forEach((controller, nav) => {
      if (!nav.isConnected) {
        controller.destroy();
        controllers.delete(nav);
      }
    });
    root.querySelectorAll(selector).forEach((nav) => {
      if (!controllers.has(nav)) controllers.set(nav, new NovaDesktopNavigation(nav));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller, nav) => {
      if (event.target.contains(nav)) {
        controller.destroy();
        controllers.delete(nav);
      }
    });
  });
})();
